import { useState, useMemo } from 'react';
import { Save, AlertCircle, Info } from 'lucide-react';
import { CategorySection } from './CategorySection';

interface TokenDefinition {
  label: string;
  description?: string;
  category: string;
  editable: string;
  type: 'color' | 'fontFamily' | 'size' | 'shadow' | 'number';
  default: string | number;
  control?: {
    type: 'slider' | 'dropdown' | 'preset';
    options?: Array<{ value: string; label: string }>;
    min?: number;
    max?: number;
    step?: number;
  };
}

interface TokensTabProps {
  tokenDefinitions: Record<string, TokenDefinition>;
  tokenDefaults: Record<string, string | number>;
  onSave: (updatedDefaults: Record<string, string | number>) => Promise<void>;
  readOnly?: boolean;
}

export function TokensTab({
  tokenDefinitions,
  tokenDefaults,
  onSave,
  readOnly = false,
}: TokensTabProps) {
  // Local state for editing
  const [localDefaults, setLocalDefaults] = useState(tokenDefaults);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Editable level filters
  const [showClient, setShowClient] = useState(true);
  const [showAuto, setShowAuto] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [showFixed, setShowFixed] = useState(false);

  // Filter tokens by editable level and group by category
  const tokensByCategory = useMemo(() => {
    // Build set of allowed editable levels
    const allowedLevels = new Set<string>();
    if (showClient) allowedLevels.add('client');
    if (showAuto) allowedLevels.add('auto');
    if (showAdvanced) allowedLevels.add('advanced');
    if (showFixed) allowedLevels.add('fixed');

    const filteredTokens = Object.entries(tokenDefinitions)
      .filter(([_, def]) => allowedLevels.has(def.editable));

    // Group by category
    const grouped: Record<string, Array<[string, TokenDefinition]>> = {};

    filteredTokens.forEach(([id, definition]) => {
      const category = definition.category || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push([id, definition]);
    });

    // Sort categories by predefined order
    const categoryOrder = ['colors', 'typography', 'spacing', 'borders', 'shadows', 'layout'];
    const sortedGrouped: Record<string, Array<[string, TokenDefinition]>> = {};

    categoryOrder.forEach((cat) => {
      if (grouped[cat]) {
        sortedGrouped[cat] = grouped[cat];
      }
    });

    // Add any remaining categories
    Object.keys(grouped).forEach((cat) => {
      if (!sortedGrouped[cat]) {
        sortedGrouped[cat] = grouped[cat];
      }
    });

    return sortedGrouped;
  }, [tokenDefinitions, showClient, showAuto, showAdvanced, showFixed]);

  const handleTokenChange = (tokenId: string, value: string) => {
    setLocalDefaults((prev) => ({
      ...prev,
      [tokenId]: value,
    }));
    setHasChanges(true);
    setError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await onSave(localDefaults);
      setHasChanges(false);
    } catch (err: any) {
      setError(err.message || 'Errore durante il salvataggio dei token');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setLocalDefaults(tokenDefaults);
    setHasChanges(false);
    setError(null);
  };

  const totalVisibleTokens = Object.values(tokensByCategory).reduce(
    (sum, tokens) => sum + tokens.length,
    0
  );

  // Build active filter labels
  const activeFilters = [];
  if (showClient) activeFilters.push('Client');
  if (showAuto) activeFilters.push('Auto');
  if (showAdvanced) activeFilters.push('Advanced');
  if (showFixed) activeFilters.push('Fixed');

  return (
    <div className="tokens-tab h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Design Tokens</h2>
            <p className="text-sm text-gray-600 mt-1">
              Gestisci i token del tema ({totalVisibleTokens} visibili)
            </p>
          </div>

          {!readOnly && (
            <div className="flex items-center space-x-2">
              {hasChanges && (
                <button
                  onClick={handleReset}
                  disabled={isSaving}
                  className="btn-secondary"
                >
                  Ripristina
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className="btn-primary flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? 'Salvataggio...' : 'Salva Modifiche'}</span>
              </button>
            </div>
          )}
        </div>

        {/* Editable Level Filters */}
        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-xs font-medium text-gray-700 uppercase mb-2">Mostra Livelli di Editabilità</p>
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showClient}
                onChange={(e) => setShowClient(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Client <span className="text-xs text-gray-500">(sicuri per clienti)</span>
              </span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showAuto}
                onChange={(e) => setShowAuto(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Auto <span className="text-xs text-gray-500">(calcolati)</span>
              </span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showAdvanced}
                onChange={(e) => setShowAdvanced(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Advanced <span className="text-xs text-gray-500">(tecnici)</span>
              </span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showFixed}
                onChange={(e) => setShowFixed(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Fixed <span className="text-xs text-gray-500 font-medium">(⚠️ non modificare)</span>
              </span>
            </label>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-blue-800">
            {activeFilters.length > 0 ? (
              <p>
                Visualizzando token di livello: <strong>{activeFilters.join(', ')}</strong>.
                Usa i filtri sopra per mostrare/nascondere livelli specifici.
              </p>
            ) : (
              <p className="text-amber-700">
                ⚠️ Nessun livello selezionato. Seleziona almeno un filtro per visualizzare i token.
              </p>
            )}
          </div>
        </div>

        {/* Error Message */}
        {saveError && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium">Errore</p>
              <p className="text-sm text-red-700 mt-1">{saveError}</p>
            </div>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {Object.entries(tokensByCategory).map(([category, tokens]) => (
          <CategorySection
            key={category}
            category={category}
            tokens={tokens}
            defaults={localDefaults}
            onChange={handleTokenChange}
            readOnly={readOnly}
          />
        ))}

        {totalVisibleTokens === 0 && (
          <div className="text-center py-12">
            {activeFilters.length === 0 ? (
              <p className="text-gray-500">Seleziona almeno un livello di editabilità per visualizzare i token</p>
            ) : (
              <p className="text-gray-500">
                Nessun token trovato per i livelli selezionati: {activeFilters.join(', ')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer (sticky save button on mobile) */}
      {!readOnly && hasChanges && (
        <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 shadow-lg md:hidden">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary w-full flex items-center justify-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>{isSaving ? 'Salvataggio...' : 'Salva Modifiche'}</span>
          </button>
        </div>
      )}
    </div>
  );
}
